import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from '@/hooks/useDebounce';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { getAccountSites } from '@/features/accounts/api';
import { Button } from '@/components/ui/button';
import { EntityFormDialog } from '@/shared/components';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SubscriptionRenewalTab, SubscriptionTabsList, Tabs, TabsContent } from './SubscriptionTabs';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Check, ChevronsUpDown, ChevronRight, ChevronDown, Plus, Trash2, X } from 'lucide-react';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  useAccountsForSelect,
  useSalesUsers,
  useContractualManagerCandidates,
  useTechnicalManagerCandidates,
  useSubscriptionInvoices,
  useSubscriptionQuotes,
  useSearchInvoicesForSubscription,
  useAssignInvoicesToSubscription,
  useUnassignInvoiceFromSubscription,
  useSearchQuotesForSubscription,
  useAssignQuotesToSubscription,
  useUnassignQuoteFromSubscription,
  useSubscriptionSensors,
  useAddSensorToSubscription,
  useRemoveSensorFromSubscription,
} from '../../hooks';
import { SubscriptionSensorsTab } from '../sensors/SubscriptionSensorsTab';
import { SubscriptionLinesTab } from '../lines/SubscriptionLinesTab';
import type { SubscriptionContractFields, SubscriptionContractUpdate } from '../../lineTypes';
import { isLineActive } from '../../lineTypes';
import { SensorSelectDialog } from '../sensors/SensorSelectDialog';
import type { V4Subscription, UpdateSubscriptionV4Input, SubscriptionStatus, V4InvoiceSku, V4SubscriptionQuote } from '../../types';
import type { QuoteSensor, CreateQuoteInput, UpdateQuoteInput } from '@/features/quotes/types';
import { QuoteFormModal, useCreateQuote, useUpdateQuote } from '@/features/quotes';
import { SUBSCRIPTION_STATUSES, computeDuration } from '../../types';

const INITIAL_SKU_COUNT = 10;

function InvoiceExpandedDetail({
  invoice,
  t,
}: {
  invoice: {
    projectName: string | null;
    poNumber: string | null;
    paymentTerms: string | null;
    dueDate: string | null;
    quoteNumber: string | null;
    subtotal: string;
    taxAmount: string;
    taxCategory: string | null;
    total: string;
    currency: string;
    skus: V4InvoiceSku[];
    skuCount: number;
  };
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const [showAll, setShowAll] = useState(false);
  const skus = invoice.skus || [];
  const visibleSkus = showAll ? skus : skus.slice(0, INITIAL_SKU_COUNT);
  const hasMore = skus.length > INITIAL_SKU_COUNT;

  return (
    <div className="px-4 pb-4 space-y-3">
      {/* Metadata bar */}
      {(invoice.projectName || invoice.poNumber || invoice.paymentTerms || invoice.dueDate || invoice.quoteNumber) && (
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pt-3">
          {invoice.projectName && (
            <span><span className="font-medium">{t('invoiceTable.project')}:</span> {invoice.projectName}</span>
          )}
          {invoice.poNumber && (
            <span><span className="font-medium">{t('invoiceTable.poNumber')}:</span> {invoice.poNumber}</span>
          )}
          {invoice.paymentTerms && (
            <span><span className="font-medium">{t('invoiceTable.paymentTerms')}:</span> {invoice.paymentTerms}</span>
          )}
          {invoice.dueDate && (
            <span><span className="font-medium">{t('invoiceTable.dueDate')}:</span> {formatDate(invoice.dueDate)}</span>
          )}
          {invoice.quoteNumber && (
            <span><span className="font-medium">{t('invoiceTable.quote')}:</span> {invoice.quoteNumber}</span>
          )}
        </div>
      )}

      {/* SKU table */}
      {skus.length > 0 ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs h-8">{t('invoiceTable.sku')}</TableHead>
                <TableHead className="text-xs h-8">{t('invoiceTable.description')}</TableHead>
                <TableHead className="text-xs h-8 text-right">{t('invoiceTable.quantity')}</TableHead>
                <TableHead className="text-xs h-8 text-right">{t('invoiceTable.unitPrice')}</TableHead>
                <TableHead className="text-xs h-8 text-right">{t('invoiceTable.discount')}</TableHead>
                <TableHead className="text-xs h-8 text-right">{t('invoiceTable.lineTotal')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleSkus.map((sku, idx) => (
                <TableRow key={sku.id || idx} className="hover:bg-transparent">
                  <TableCell className="text-xs py-1.5 font-mono">{sku.sku}</TableCell>
                  <TableCell className="text-xs py-1.5 max-w-48 truncate">{sku.description || '\u2014'}</TableCell>
                  <TableCell className="text-xs py-1.5 text-right tabular-nums">{Number(sku.quantity)}</TableCell>
                  <TableCell className="text-xs py-1.5 text-right tabular-nums">{formatCurrency(Number(sku.unitPrice), invoice.currency)}</TableCell>
                  <TableCell className="text-xs py-1.5 text-right tabular-nums">{Number(sku.discount) > 0 ? `${Number(sku.discount)}%` : '\u2014'}</TableCell>
                  <TableCell className="text-xs py-1.5 text-right tabular-nums">{formatCurrency(Number(sku.total), invoice.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {hasMore && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-xs w-full"
              onClick={() => setShowAll(!showAll)}
            >
              {showAll ? t('invoiceTable.showLess') : t('invoiceTable.showAll', { count: skus.length })}
            </Button>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground pt-3">{t('invoiceTable.items', { count: 0 })}</p>
      )}

      {/* Financial footer */}
      <div className="border-t pt-2 space-y-1 text-xs">
        <div className="flex justify-between text-muted-foreground">
          <span>{t('invoiceTable.subtotal')}</span>
          <span className="tabular-nums">{formatCurrency(Number(invoice.subtotal), invoice.currency)}</span>
        </div>
        {Number(invoice.taxAmount) > 0 && (
          <div className="flex justify-between text-muted-foreground">
            <span>{t('invoiceTable.tax')}{invoice.taxCategory ? ` (${invoice.taxCategory})` : ''}</span>
            <span className="tabular-nums">{formatCurrency(Number(invoice.taxAmount), invoice.currency)}</span>
          </div>
        )}
        <div className="flex justify-between font-medium">
          <span>{t('invoiceTable.grandTotal')}</span>
          <span className="tabular-nums">{formatCurrency(Number(invoice.total), invoice.currency)}</span>
        </div>
      </div>
    </div>
  );
}

function QuoteRow({
  quote,
  t,
  onRemove,
}: {
  quote: V4SubscriptionQuote;
  t: (key: string, opts?: Record<string, unknown>) => string;
  onRemove?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const items = quote.lineItems || [];
  const visibleItems = showAll ? items : items.slice(0, INITIAL_SKU_COUNT);
  const hasMore = items.length > INITIAL_SKU_COUNT;

  return (
    <div>
      <div
        className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 text-sm"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded
          ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        }
        <Link
          to={`/erp/quotes?search=${encodeURIComponent(quote.quoteNumber)}`}
          className="text-primary hover:underline font-medium"
          onClick={(e) => e.stopPropagation()}
        >
          {quote.quoteNumber}
        </Link>
        <span className="text-muted-foreground">
          {quote.createdDate ? formatDate(quote.createdDate) : '\u2014'}
        </span>
        <span className="ml-auto tabular-nums font-medium">
          {formatCurrency(Number(quote.total), quote.currency)}
        </span>
        <Badge variant="secondary" className="text-xs">
          {quote.status}
        </Badge>
        {quote.lineItemCount > 0 && (
          <span className="text-xs text-muted-foreground">
            {t('quoteTable.items', { count: quote.lineItemCount })}
          </span>
        )}
        {onRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {expanded && (
        <div className="bg-muted/20 border-t px-4 pb-4 space-y-3">
          {(quote.projectName || quote.poNumber || quote.expirationDate) && (
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground pt-3">
              {quote.projectName && (
                <span><span className="font-medium">{t('quoteTable.project')}:</span> {quote.projectName}</span>
              )}
              {quote.poNumber && (
                <span><span className="font-medium">{t('quoteTable.poNumber')}:</span> {quote.poNumber}</span>
              )}
              {quote.expirationDate && (
                <span><span className="font-medium">{t('quoteTable.expirationDate')}:</span> {formatDate(quote.expirationDate)}</span>
              )}
            </div>
          )}

          {items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs h-8">{t('quoteTable.sku')}</TableHead>
                    <TableHead className="text-xs h-8">{t('quoteTable.description')}</TableHead>
                    <TableHead className="text-xs h-8 text-right">{t('quoteTable.quantity')}</TableHead>
                    <TableHead className="text-xs h-8 text-right">{t('quoteTable.unitPrice')}</TableHead>
                    <TableHead className="text-xs h-8 text-right">{t('quoteTable.discount')}</TableHead>
                    <TableHead className="text-xs h-8 text-right">{t('quoteTable.lineTotal')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleItems.map((item, idx) => (
                    <TableRow key={item.id || idx} className="hover:bg-transparent">
                      <TableCell className="text-xs py-1.5 font-mono">{item.sku || '\u2014'}</TableCell>
                      <TableCell className="text-xs py-1.5 max-w-48 truncate">{item.description || item.name || '\u2014'}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums">{Number(item.quantity)}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums">{formatCurrency(Number(item.unitPrice), quote.currency)}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums">{Number(item.discount) > 0 ? `${Number(item.discount)}%` : '\u2014'}</TableCell>
                      <TableCell className="text-xs py-1.5 text-right tabular-nums">{formatCurrency(Number(item.total), quote.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {hasMore && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-xs w-full"
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? t('quoteTable.showLess') : t('quoteTable.showAll', { count: items.length })}
                </Button>
              )}
            </>
          ) : (
            <p className="text-xs text-muted-foreground pt-3">{t('quoteTable.items', { count: 0 })}</p>
          )}

          <div className="border-t pt-2 space-y-1 text-xs">
            <div className="flex justify-between text-muted-foreground">
              <span>{t('quoteTable.subtotal')}</span>
              <span className="tabular-nums">{formatCurrency(Number(quote.subtotal), quote.currency)}</span>
            </div>
            {Number(quote.taxAmount) > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>{t('quoteTable.tax')}</span>
                <span className="tabular-nums">{formatCurrency(Number(quote.taxAmount), quote.currency)}</span>
              </div>
            )}
            {Number(quote.discount) > 0 && (
              <div className="flex justify-between text-muted-foreground">
                <span>{t('quoteTable.discount')}</span>
                <span className="tabular-nums">-{formatCurrency(Number(quote.discount), quote.currency)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium">
              <span>{t('quoteTable.grandTotal')}</span>
              <span className="tabular-nums">{formatCurrency(Number(quote.total), quote.currency)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface SubscriptionFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contract: V4Subscription | null;
  onSubmit: (data: UpdateSubscriptionV4Input) => Promise<void>;
  onDelete?: () => void;
  isSubmitting?: boolean;
}

export function SubscriptionFormModal({
  open,
  onOpenChange,
  contract,
  onSubmit,
  onDelete,
  isSubmitting,
}: SubscriptionFormModalProps) {
  const { t } = useTranslation('subscriptions');
  const [form, setForm] = useState<UpdateSubscriptionV4Input & SubscriptionContractUpdate>({});
  const [activeTab, setActiveTab] = useState('details');
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const debouncedAccountSearch = useDebounce(accountSearch, 300);
  const [contractualManagerOpen, setContractualManagerOpen] = useState(false);
  const [technicalManagerOpen, setTechnicalManagerOpen] = useState(false);

  const [invoiceSearchOpen, setInvoiceSearchOpen] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const debouncedInvoiceSearch = useDebounce(invoiceSearch, 300);
  const [invoiceSearchPage, setInvoiceSearchPage] = useState(0);
  const [expandedInvoiceId, setExpandedInvoiceId] = useState<string | null>(null);
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'recurring' | 'non-recurring'>('all');

  const [quoteSearchOpen, setQuoteSearchOpen] = useState(false);
  const [quoteSearch, setQuoteSearch] = useState('');
  const debouncedQuoteSearch = useDebounce(quoteSearch, 300);
  const [quoteSearchPage, setQuoteSearchPage] = useState(0);

  const accountsQuery = useAccountsForSelect(debouncedAccountSearch || undefined);
  const salesUsersQuery = useSalesUsers();
  const contractualManagersQuery = useContractualManagerCandidates(
    form.accountId || contract?.accountId || undefined,
  );
  const technicalManagersQuery = useTechnicalManagerCandidates(
    form.accountId || contract?.accountId || undefined,
  );
  const invoicesQuery = useSubscriptionInvoices(contract?.id || null);
  const quotesQuery = useSubscriptionQuotes(contract?.id || null);
  const invoiceSearchQuery = useSearchInvoicesForSubscription(
    debouncedInvoiceSearch || undefined,
    contract?.accountId || undefined,
    invoiceSearchPage,
  );
  const assignMutation = useAssignInvoicesToSubscription();
  const unassignMutation = useUnassignInvoiceFromSubscription();

  const quoteSearchQuery = useSearchQuotesForSubscription(
    debouncedQuoteSearch || undefined,
    contract?.accountId || undefined,
    quoteSearchPage,
  );
  const assignQuoteMutation = useAssignQuotesToSubscription();
  const unassignQuoteMutation = useUnassignQuoteFromSubscription();

  // ── New quote (create a quote for this subscription's account, Hugo #1084 p5) ──
  const [newQuoteOpen, setNewQuoteOpen] = useState(false);
  const createQuoteMutation = useCreateQuote();
  const updateQuoteMutation = useUpdateQuote();
  // Once the first save creates the quote (keep-open flow), later saves UPDATE it —
  // never create a duplicate — and it is linked to this subscription exactly once.
  const createdQuotePgIdRef = useRef<string | null>(null);
  const handleCreateSubscriptionQuote = async (data: CreateQuoteInput | UpdateQuoteInput) => {
    if (createdQuotePgIdRef.current) {
      return updateQuoteMutation.mutateAsync({ id: createdQuotePgIdRef.current, data: data as UpdateQuoteInput });
    }
    const created = await createQuoteMutation.mutateAsync(data as CreateQuoteInput);
    createdQuotePgIdRef.current = created?.id ?? created?._id ?? null;
    if (createdQuotePgIdRef.current && contract?.id) {
      await assignQuoteMutation.mutateAsync({ subscriptionId: contract.id, quoteIds: [createdQuotePgIdRef.current] });
    }
    return created;
  };

  // ── Sensors state ──────────────────────────────────────────────
  const [sensorSelectOpen, setSensorSelectOpen] = useState(false);
  const sensorsQuery = useSubscriptionSensors(contract?.subscriptionNumber);
  const addSensorMutation = useAddSensorToSubscription();
  const removeSensorMutation = useRemoveSensorFromSubscription();
  const subscriptionSensors = sensorsQuery.data || [];

  // Account sites for the "default billing site" picker (Hugo #1084 p6).
  const accountSitesQuery = useQuery({
    queryKey: ['subscription-account-sites', contract?.accountId],
    queryFn: () => getAccountSites(contract!.accountId, { pageLength: 100 }),
    enabled: open && !!contract?.accountId,
  });
  const accountSites = accountSitesQuery.data?.data || [];

  const linkedSensorIds = new Set<string>(subscriptionSensors.map((s) => s.mongoSensorId));

  const handleAddSensor = (sensor: QuoteSensor) => {
    if (!contract?.subscriptionNumber) return;
    addSensorMutation.mutate({
      sensorId: sensor._id,
      subscriptionNumber: contract.subscriptionNumber,
      sensor: {
        id: sensor._id,
        mongoSensorId: sensor._id,
        name: sensor.name,
        status: (sensor.status as number) ?? null,
        customId: (sensor.customid as string) ?? null,
        streamName: (sensor.stream as { name?: string })?.name ?? null,
        invoices: [],
        quotes: [],
        subscriptions: [contract.subscriptionNumber],
        site: null,
        location: sensor.location ? { id: '', name: (sensor.location as { name: string }).name } : null,
      },
    });
  };

  const handleRemoveSensor = (sensorId: string) => {
    if (!contract?.subscriptionNumber) return;
    removeSensorMutation.mutate({
      sensorId,
      subscriptionNumber: contract.subscriptionNumber,
    });
  };

  const handleRemoveAllSensors = () => {
    if (!contract?.subscriptionNumber) return;
    subscriptionSensors.forEach((s) => {
      removeSensorMutation.mutate({
        sensorId: s.id,
        subscriptionNumber: contract.subscriptionNumber,
      });
    });
  };

  const accounts = accountsQuery.data || [];
  const salesUsers = salesUsersQuery.data || [];
  const contractualManagerCandidates = contractualManagersQuery.data || [];
  const technicalManagerCandidates = technicalManagersQuery.data || [];
  const invoices = invoicesQuery.data || [];
  const filteredInvoices = invoiceFilter === 'all'
    ? invoices
    : invoiceFilter === 'recurring'
      ? invoices.filter((inv) => inv.dealType === 'recurring')
      : invoices.filter((inv) => inv.dealType !== 'recurring');
  const quotes = quotesQuery.data || [];

  // Build case-insensitive lookup map for manager names from contract + invoice data
  const managerMap = new Map<string, { name: string; email?: string | null }>();
  for (const m of contract?.contractualManagers || []) {
    managerMap.set(m.id.toLowerCase(), m);
  }
  for (const m of contract?.technicalManagers || []) {
    if (!managerMap.has(m.id.toLowerCase())) managerMap.set(m.id.toLowerCase(), m);
  }
  for (const m of contractualManagerCandidates) {
    if (!managerMap.has(m.id.toLowerCase())) managerMap.set(m.id.toLowerCase(), m);
  }
  for (const m of technicalManagerCandidates) {
    if (!managerMap.has(m.id.toLowerCase())) managerMap.set(m.id.toLowerCase(), m);
  }
  // Also populate from invoice-resolved manager info (for newly prefilled IDs)
  for (const inv of invoices) {
    if (inv.contractualManagerId && inv.contractualManagerName && !managerMap.has(inv.contractualManagerId.toLowerCase())) {
      managerMap.set(inv.contractualManagerId.toLowerCase(), {
        name: inv.contractualManagerName,
        email: inv.contractualManagerEmail,
      });
    }
  }
  const getManagerDisplay = (id: string) => {
    const info = managerMap.get(id.toLowerCase());
    if (!info || info.name === id) return id; // not resolved
    return info.email ? `${info.name} (${info.email})` : info.name;
  };

  // Filter out invoices already assigned to this subscription from search results
  const assignedIds = new Set(invoices.map((inv) => inv.id));
  const searchResults = (invoiceSearchQuery.data?.invoices || []).filter(
    (inv) => !assignedIds.has(inv.id) && !inv.subscriptionId,
  );
  const searchTotal = invoiceSearchQuery.data?.total ?? 0;

  // Filter out quotes already assigned to this subscription from search results
  const assignedQuoteIds = new Set(quotes.map((q) => q.id));
  const quoteSearchResults = (quoteSearchQuery.data?.quotes || []).filter(
    (q) => !assignedQuoteIds.has(q.id) && !q.subscriptionId,
  );
  const quoteSearchTotal = quoteSearchQuery.data?.total ?? 0;

  // Initialize form when contract loads
  useEffect(() => {
    if (!contract || !open) return;

    setForm({
      name: contract.name || '',
      accountId: contract.accountId || '',
      salesManagerId: contract.salesManagerId || '',
      contractualManagerIds: contract.contractualManagerIds || [],
      technicalManagerIds: contract.technicalManagerIds || [],
      currency: contract.currency || 'CAD',
      paymentTerms: contract.paymentTerms || '',
      annualValue: contract.annualValue ?? '',
      totalValue: contract.totalValue ?? '',
      startDate: contract.startDate?.split('T')[0] || '',
      endDate: contract.endDate?.split('T')[0] || '',
      signedDate: contract.signedDate?.split('T')[0] || '',
      // #1397 D1 — empty means "derive from the start-date anniversary". Seeding '' (not
      // undefined) keeps the field controlled, so clearing it sends '' and the DAO nulls
      // the column rather than leaving the old override in place.
      billingDate: contract.billingDate?.split('T')[0] || '',
      // #1402 rider B — TRUE prepaid only: paid up front as a block, so no renewal
      // offer before endDate. Never inferred from the end date alone (annual
      // multi-year contracts keep their yearly windows).
      prepaid: contract.prepaid || false,
      autoRenew: contract.autoRenew || false,
      renewalNoticeDays: contract.renewalNoticeDays || 90,
      endNoticeDays: (contract as SubscriptionContractFields).endNoticeDays ?? 180,
      gdriveFolderId: contract.gdriveFolderId || '',
      defaultBillingSiteId: contract.defaultBillingSiteId || '',
      contractualClause: contract.contractualClause || '',
      status: contract.status,
      description: contract.description || '',
    });
    setActiveTab('details');
  }, [contract, open]);

  // Prefill managers from linked invoices when invoice data changes
  useEffect(() => {
    if (!invoices.length) return;

    const currentContractual = new Set((form.contractualManagerIds || []) as string[]);
    const invoiceContractualIds = [...new Set(
      invoices.map((inv) => inv.contractualManagerId).filter(Boolean) as string[],
    )];
    const newContractual = invoiceContractualIds.filter((id) => !currentContractual.has(id));

    if (newContractual.length > 0) {
      setForm((prev) => ({
        ...prev,
        contractualManagerIds: [...(prev.contractualManagerIds || []), ...newContractual],
      }));
    }
  }, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch {
      // Error already handled by mutation onError toast
    }
  };

  // Multi-select helpers
  const addToList = (field: 'contractualManagerIds' | 'technicalManagerIds', value: string) => {
    setForm((currentForm) => {
      const current = (currentForm[field] || []) as string[];
      if (current.includes(value)) return currentForm;
      return { ...currentForm, [field]: [...current, value] };
    });
  };

  const removeFromList = (field: 'contractualManagerIds' | 'technicalManagerIds', value: string) => {
    setForm((currentForm) => {
      const current = (currentForm[field] || []) as string[];
      return { ...currentForm, [field]: current.filter((v) => v !== value) };
    });
  };

  if (!contract) return null;

  // #1407 — Lines-tab counter: verified/active counts. Ended lines never need
  // verifying, so both counts run on ACTIVE lines only (the renewal gate's rule).
  const contractLines = (contract as SubscriptionContractFields).lines;
  // Bare `filter(isLineActive)` would pass the array INDEX as the `today` param.
  const activeContractLines = contractLines?.filter((l) => isLineActive(l));

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="5xl"
      title={t('form.editTitle', { number: contract.subscriptionNumber })}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      saveLabel={isSubmitting ? t('form.saving') : t('form.save')}
      cancelLabel={t('form.close')}
      footerExtras={
        onDelete ? (
          <Button type="button" variant="destructive" onClick={onDelete}>
            {t('form.delete')}
          </Button>
        ) : undefined
      }
    >
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
            <SubscriptionTabsList
              verifiedLines={activeContractLines?.filter((l) => l.verified).length}
              activeLines={activeContractLines?.length}
            />

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 mt-4">
              {/* Subscription Number (read-only) */}
              <div className="grid gap-2">
                <Label>{t('fields.contractNumber')}</Label>
                <Input value={contract.subscriptionNumber} disabled />
              </div>

              {/* Status */}
              <div className="grid gap-2">
                <Label>{t('fields.status')}</Label>
                <Select
                  value={form.status || 'draft'}
                  onValueChange={(v) => setForm({ ...form, status: v as SubscriptionStatus })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBSCRIPTION_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`status.${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Name */}
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('fields.name')}</Label>
                <Input
                  id="edit-name"
                  value={form.name || ''}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div className="grid gap-2">
                <Label htmlFor="edit-description">{t('fields.description')}</Label>
                <Input
                  id="edit-description"
                  value={form.description || ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>

              {/* Account */}
              <div className="grid gap-2">
                <Label>{t('fields.account')}</Label>
                <Popover open={accountSearchOpen} onOpenChange={setAccountSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={accountSearchOpen}
                      className="w-full justify-between font-normal"
                    >
                      {form.accountId
                        ? accounts.find((a) => a.id === form.accountId)?.name
                          ? `${accounts.find((a) => a.id === form.accountId)!.name} (${accounts.find((a) => a.id === form.accountId)!.accountNumber})`
                          : contract?.accountName || form.accountId
                        : t('fields.selectAccount')}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t('fields.selectAccount')}
                        value={accountSearch}
                        onValueChange={setAccountSearch}
                      />
                      <CommandList>
                        <CommandEmpty>{accountsQuery.isLoading ? t('filters.loading') : 'No accounts found'}</CommandEmpty>
                        <CommandGroup>
                          {accounts.map((a) => (
                            <CommandItem
                              key={a.id}
                              value={a.id}
                              onSelect={() => {
                                setForm({ ...form, accountId: a.id });
                                setAccountSearchOpen(false);
                                setAccountSearch('');
                              }}
                            >
                              <Check className={cn('mr-2 h-4 w-4', form.accountId === a.id ? 'opacity-100' : 'opacity-0')} />
                              {a.name} ({a.accountNumber})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Account Manager */}
              <div className="grid gap-2">
                <Label>{t('fields.salesManager')}</Label>
                <Select
                  value={form.salesManagerId || ''}
                  onValueChange={(v) => setForm({ ...form, salesManagerId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectSalesManager')} />
                  </SelectTrigger>
                  <SelectContent>
                    {salesUsers.map((u) => (
                      <SelectItem key={u._id} value={u._id}>
                        {[u.firstname, u.lastname].filter(Boolean).join(' ') || u.email || u._id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Currency & Payment Terms */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('fields.currency')}</Label>
                  <Select
                    value={form.currency || 'CAD'}
                    onValueChange={(v) => setForm({ ...form, currency: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>{t('fields.paymentTerms')}</Label>
                  <Select
                    value={form.paymentTerms || 'none'}
                    onValueChange={(v) => setForm({ ...form, paymentTerms: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t('fields.selectPaymentTerms')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('paymentTerms.none')}</SelectItem>
                      <SelectItem value="on-order">{t('paymentTerms.on-order')}</SelectItem>
                      <SelectItem value="net-0">{t('paymentTerms.net-0')}</SelectItem>
                      <SelectItem value="net-30">{t('paymentTerms.net-30')}</SelectItem>
                      <SelectItem value="net-45">{t('paymentTerms.net-45')}</SelectItem>
                      <SelectItem value="net-60">{t('paymentTerms.net-60')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Annual & total contract value (#1409) */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('fields.annualValue')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.annualValue ?? ''}
                    onChange={(e) => setForm({ ...form, annualValue: e.target.value || null })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('fields.totalValue')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.totalValue ?? ''}
                    onChange={(e) => setForm({ ...form, totalValue: e.target.value || null })}
                  />
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('fields.startDate')}</Label>
                  <Input
                    type="date"
                    value={form.startDate || ''}
                    onChange={(e) => setForm({ ...form, startDate: e.target.value || undefined })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('fields.endDate')}</Label>
                  <Input
                    type="date"
                    value={form.endDate || ''}
                    onChange={(e) => setForm({ ...form, endDate: e.target.value || undefined })}
                  />
                </div>
              </div>

              {/* Duration (computed, read-only) */}
              <div className="grid gap-2">
                <Label>{t('fields.duration')}</Label>
                <div className="h-9 px-3 py-2 text-sm text-muted-foreground bg-muted/50 rounded-md">
                  {computeDuration(form.startDate || null, form.endDate || null) || '\u2014'}
                </div>
              </div>

              {/* Signed Date */}
              <div className="grid gap-2">
                <Label>{t('fields.signedDate')}</Label>
                <Input
                  type="date"
                  value={form.signedDate || ''}
                  onChange={(e) => setForm({ ...form, signedDate: e.target.value || undefined })}
                />
              </div>

              {/* Billing date — the AM override of the derived anniversary (#1397 D1).
                  Empty is the normal state: the renewal window is computed from the
                  start date. Filling it in pins the cycle for subscriptions whose real
                  billing day does not follow their start date. */}
              <div className="grid gap-2">
                <Label>{t('fields.billingDate', 'Billing date (override)')}</Label>
                <Input
                  type="date"
                  data-testid="subscription-billing-date"
                  value={form.billingDate || ''}
                  onChange={(e) => setForm({ ...form, billingDate: e.target.value || null })}
                />
                <p className="text-xs text-muted-foreground">
                  {t(
                    'fields.billingDateHint',
                    'Leave empty to use the anniversary of the start date. Set a date only to override it.',
                  )}
                </p>
              </div>

              {/* #1402 rider B — explicit prepaid marker. The renewal engine gates on
                  this + the end date; the end date alone never blocks a renewal. */}
              <div className="grid gap-2">
                <Label>{t('fields.prepaid', 'Prepaid (no renewal before end date)')}</Label>
                <Select
                  value={form.prepaid ? 'true' : 'false'}
                  onValueChange={(v) => setForm({ ...form, prepaid: v === 'true' })}
                >
                  <SelectTrigger data-testid="subscription-prepaid">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{t('common:yes', 'Yes')}</SelectItem>
                    <SelectItem value="false">{t('common:no', 'No')}</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {t(
                    'fields.prepaidHint',
                    'Yes only for contracts paid up front as a block: no renewal offer is made before the end date. Multi-year contracts billed annually stay No.',
                  )}
                </p>
              </div>

              {/* Auto Renew & Renewal Notice Days */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label>{t('fields.autoRenew')}</Label>
                  <Select
                    value={form.autoRenew ? 'true' : 'false'}
                    onValueChange={(v) => setForm({ ...form, autoRenew: v === 'true' })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">{t('common:yes', 'Yes')}</SelectItem>
                      <SelectItem value="false">{t('common:no', 'No')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>{t('fields.renewalNoticeDays')}</Label>
                  <Input
                    type="number"
                    value={form.renewalNoticeDays ?? 90}
                    onChange={(e) => setForm({ ...form, renewalNoticeDays: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>{t('fields.endNoticeDays')}</Label>
                  <Input
                    type="number"
                    min={0}
                    data-testid="end-notice-days"
                    value={form.endNoticeDays ?? 180}
                    onChange={(e) => setForm({ ...form, endNoticeDays: parseInt(e.target.value) || undefined })}
                  />
                </div>
              </div>

              {/* Google Drive Folder ID */}
              <div className="grid gap-2">
                <Label>{t('fields.gdriveLink')}</Label>
                <Input
                  value={form.gdriveFolderId || ''}
                  onChange={(e) => setForm({ ...form, gdriveFolderId: e.target.value || undefined })}
                  placeholder="e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                />
              </div>
            </TabsContent>

            {/* Managers Tab */}
            <TabsContent value="managers" className="space-y-4 mt-4">
              {/* Default billing site (Hugo #1084 p6) — the account site quotes/invoices for
                  this subscription bill to by default */}
              <div className="grid gap-2">
                <Label>{t('fields.defaultBillingSite', 'Default billing site')}</Label>
                <Select
                  value={form.defaultBillingSiteId || 'none'}
                  onValueChange={(v) => setForm({ ...form, defaultBillingSiteId: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('fields.selectSite', 'Select a site…')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('fields.noDefaultSite', 'No default')}</SelectItem>
                    {accountSites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Contractual Managers */}
              <div className="grid gap-2">
                <Label>{t('fields.contractualManagers')}</Label>
                <div className="flex flex-wrap gap-1">
                  {(form.contractualManagerIds || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-1">{t('fields.noManagers', 'None assigned')}</p>
                  ) : (
                    (form.contractualManagerIds || []).map((id) => (
                      <Badge key={id} variant="secondary" className="gap-1">
                        {getManagerDisplay(id)}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeFromList('contractualManagerIds', id)} />
                      </Badge>
                    ))
                  )}
                </div>
                <Popover open={contractualManagerOpen} onOpenChange={setContractualManagerOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="w-fit gap-1">
                      <Plus className="h-4 w-4" />
                      {t('fields.addContractualManager', 'Add contractual manager...')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('fields.addContractualManager', 'Add contractual manager...')} />
                      <CommandList>
                        <CommandEmpty>
                          {contractualManagersQuery.isLoading
                            ? t('filters.loading', 'Loading...')
                            : t('fields.noContractualManagersFound', 'No contractual managers found')}
                        </CommandEmpty>
                        <CommandGroup>
                          {contractualManagerCandidates
                            .filter((manager) => !(form.contractualManagerIds || []).includes(manager.id))
                            .map((manager) => (
                              <CommandItem
                                key={manager.id}
                                value={`${manager.name} ${manager.email || ''}`}
                                onSelect={() => {
                                  addToList('contractualManagerIds', manager.id);
                                  setContractualManagerOpen(false);
                                }}
                              >
                                <div className="min-w-0">
                                  <div className="truncate">{manager.name}</div>
                                  {manager.email && (
                                    <div className="truncate text-xs text-muted-foreground">
                                      {manager.email}
                                    </div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Technical Managers */}
              <div className="grid gap-2">
                <Label>{t('fields.technicalManagers')}</Label>
                <div className="flex flex-wrap gap-1">
                  {(form.technicalManagerIds || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-1">{t('fields.noManagers', 'None assigned')}</p>
                  ) : (
                    (form.technicalManagerIds || []).map((id) => (
                      <Badge key={id} variant="secondary" className="gap-1">
                        {getManagerDisplay(id)}
                        <X className="h-3 w-3 cursor-pointer" onClick={() => removeFromList('technicalManagerIds', id)} />
                      </Badge>
                    ))
                  )}
                </div>
                <Popover open={technicalManagerOpen} onOpenChange={setTechnicalManagerOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="w-fit gap-1">
                      <Plus className="h-4 w-4" />
                      {t('fields.addTechnicalManager', 'Add technical manager...')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('fields.addTechnicalManager', 'Add technical manager...')} />
                      <CommandList>
                        <CommandEmpty>
                          {technicalManagersQuery.isLoading
                            ? t('filters.loading', 'Loading...')
                            : t('fields.noTechnicalManagersFound', 'No technical managers found')}
                        </CommandEmpty>
                        <CommandGroup>
                          {technicalManagerCandidates
                            .filter((manager) => !(form.technicalManagerIds || []).includes(manager.id))
                            .map((manager) => (
                              <CommandItem
                                key={manager.id}
                                value={`${manager.name} ${manager.email || ''}`}
                                onSelect={() => {
                                  addToList('technicalManagerIds', manager.id);
                                  setTechnicalManagerOpen(false);
                                }}
                              >
                                <div className="min-w-0">
                                  <div className="truncate">{manager.name}</div>
                                  {manager.email && (
                                    <div className="truncate text-xs text-muted-foreground">
                                      {manager.email}
                                    </div>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </TabsContent>

            {/* Invoices Tab */}
            <TabsContent value="invoices" className="space-y-4 mt-4">
              {/* Add Invoice Search Dropdown */}
              <Popover open={invoiceSearchOpen} onOpenChange={(open) => {
                setInvoiceSearchOpen(open);
                if (!open) { setInvoiceSearch(''); setInvoiceSearchPage(0); }
              }}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1">
                    <Plus className="h-4 w-4" />
                    {t('invoiceTable.addInvoice')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('invoiceTable.searchInvoices')}
                      value={invoiceSearch}
                      onValueChange={(v) => { setInvoiceSearch(v); setInvoiceSearchPage(0); }}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {invoiceSearchQuery.isLoading ? t('filters.loading') : (
                          <div className="py-2 text-center">
                            <p>{t('invoiceTable.noSearchResults')}</p>
                            {debouncedInvoiceSearch && (
                              <p className="mt-1 text-xs text-muted-foreground px-4">
                                {t('invoiceTable.noSearchResultsHint')}
                              </p>
                            )}
                          </div>
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {searchResults.map((inv) => (
                          <CommandItem
                            key={inv.id}
                            value={inv.id}
                            onSelect={() => {
                              if (contract) {
                                assignMutation.mutate({ subscriptionId: contract.id, invoiceIds: [inv.id] });
                              }
                            }}
                          >
                            <span className="font-medium">#{inv.invoiceNumber}</span>
                            <Badge variant="secondary" className="ml-2 text-xs">{inv.status}</Badge>
                            <span className="ml-auto text-sm text-muted-foreground">
                              {formatCurrency(Number(inv.total), inv.currency)}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    {searchTotal > 100 && (
                      <div className="flex items-center justify-between border-t px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={invoiceSearchPage === 0}
                          onClick={() => setInvoiceSearchPage((p) => Math.max(0, p - 1))}
                        >
                          {t('table.previous')}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {t('table.pageOf', { current: invoiceSearchPage + 1, total: Math.ceil(searchTotal / 100) })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={(invoiceSearchPage + 1) * 100 >= searchTotal}
                          onClick={() => setInvoiceSearchPage((p) => p + 1)}
                        >
                          {t('table.next')}
                        </Button>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Invoice Type Filter */}
              {invoices.length > 0 && (
                <div className="flex gap-1">
                  {(['all', 'recurring', 'non-recurring'] as const).map((filter) => (
                    <Button
                      key={filter}
                      type="button"
                      variant={invoiceFilter === filter ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setInvoiceFilter(filter)}
                      className="text-xs h-7"
                    >
                      {t(`invoiceTable.filter${filter === 'all' ? 'All' : filter === 'recurring' ? 'Recurring' : 'NonRecurring'}`)}
                    </Button>
                  ))}
                </div>
              )}

              {/* Invoice Table */}
              {invoicesQuery.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : filteredInvoices.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('invoiceTable.noInvoices')}
                </p>
              ) : (
                <div className="border rounded-md divide-y">
                  {filteredInvoices.map((inv) => {
                    const isExpanded = expandedInvoiceId === inv.id;
                    return (
                      <div key={inv.id}>
                        {/* Collapsed row */}
                        <div
                          className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-muted/50 text-sm"
                          onClick={() => setExpandedInvoiceId(isExpanded ? null : inv.id)}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                            : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                          }
                          <Link
                            to={`/erp/invoices?search=${encodeURIComponent(inv.invoiceNumber)}`}
                            className="text-primary hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {inv.invoiceNumber}
                          </Link>
                          <span className="text-muted-foreground">
                            {inv.invoiceDate ? formatDate(inv.invoiceDate) : '\u2014'}
                          </span>
                          <span className="ml-auto tabular-nums font-medium">
                            {formatCurrency(Number(inv.total), inv.currency)}
                          </span>
                          <Badge variant="secondary" className="text-xs">
                            {inv.status}
                          </Badge>
                          {inv.skuCount > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {t('invoiceTable.items', { count: inv.skuCount })}
                            </span>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (contract) {
                                unassignMutation.mutate({ subscriptionId: contract.id, invoiceId: inv.id });
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="bg-muted/20 border-t">
                            <InvoiceExpandedDetail invoice={inv} t={t} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            {/* Quotes Tab */}
            <TabsContent value="quotes" className="space-y-4 mt-4">
              <div className="flex items-center gap-2 flex-wrap">
              {/* Add Quote Search Dropdown */}
              <Popover open={quoteSearchOpen} onOpenChange={(open) => {
                setQuoteSearchOpen(open);
                if (!open) { setQuoteSearch(''); setQuoteSearchPage(0); }
              }}>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1">
                    <Plus className="h-4 w-4" />
                    {t('quoteTable.addQuote')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start" onWheel={(e) => e.stopPropagation()}>
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder={t('quoteTable.searchQuotes')}
                      value={quoteSearch}
                      onValueChange={(v) => { setQuoteSearch(v); setQuoteSearchPage(0); }}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {quoteSearchQuery.isLoading ? t('filters.loading') : (
                          <div className="py-2 text-center">
                            <p>{t('quoteTable.noSearchResults')}</p>
                            {debouncedQuoteSearch && (
                              <p className="mt-1 text-xs text-muted-foreground px-4">
                                {t('quoteTable.noSearchResultsHint')}
                              </p>
                            )}
                          </div>
                        )}
                      </CommandEmpty>
                      <CommandGroup>
                        {quoteSearchResults.map((q) => (
                          <CommandItem
                            key={q.id}
                            value={q.id}
                            onSelect={() => {
                              if (contract) {
                                assignQuoteMutation.mutate({ subscriptionId: contract.id, quoteIds: [q.id] });
                              }
                            }}
                          >
                            <span className="font-medium">#{q.quoteNumber}</span>
                            <Badge variant="secondary" className="ml-2 text-xs">{q.status}</Badge>
                            <span className="ml-auto text-sm text-muted-foreground">
                              {formatCurrency(Number(q.total), q.currency)}
                            </span>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                    {quoteSearchTotal > 100 && (
                      <div className="flex items-center justify-between border-t px-3 py-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={quoteSearchPage === 0}
                          onClick={() => setQuoteSearchPage((p) => Math.max(0, p - 1))}
                        >
                          {t('table.previous')}
                        </Button>
                        <span className="text-xs text-muted-foreground">
                          {t('table.pageOf', { current: quoteSearchPage + 1, total: Math.ceil(quoteSearchTotal / 100) })}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={(quoteSearchPage + 1) * 100 >= quoteSearchTotal}
                          onClick={() => setQuoteSearchPage((p) => p + 1)}
                        >
                          {t('table.next')}
                        </Button>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
              {contract?.accountId && (
                <Button type="button" size="sm" className="gap-1" onClick={() => setNewQuoteOpen(true)}>
                  <Plus className="h-4 w-4" />
                  {t('quoteTable.newQuote')}
                </Button>
              )}
              </div>

              {/* Quote List */}
              {quotesQuery.isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : quotes.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  {t('quoteTable.noQuotes')}
                </p>
              ) : (
                <div className="border rounded-md divide-y">
                  {quotes.map((quote) => (
                    <QuoteRow
                      key={quote.id}
                      quote={quote}
                      t={t}
                      onRemove={() => {
                        if (contract) {
                          unassignQuoteMutation.mutate({ subscriptionId: contract.id, quoteId: quote.id });
                        }
                      }}
                    />
                  ))}
                </div>
              )}
            </TabsContent>

            <SubscriptionRenewalTab subscriptionId={contract?.id ?? null} onGoToLines={() => setActiveTab('lines')} />

            <TabsContent value="lines">
              {contract && (
                <SubscriptionLinesTab
                  subscriptionId={contract.id}
                  currency={contract.currency}
                  lines={(contract as SubscriptionContractFields).lines}
                  coveredAccounts={(contract as SubscriptionContractFields).coveredAccounts}
                  ceiling={(contract as SubscriptionContractFields).ceiling}
                />
              )}
            </TabsContent>

            {/* Sensors Tab */}
            <TabsContent value="sensors">
              <SubscriptionSensorsTab
                sensors={subscriptionSensors}
                isLoading={sensorsQuery.isLoading}
                onSelectSensors={() => setSensorSelectOpen(true)}
                onRemoveSensor={handleRemoveSensor}
                onRemoveAllSensors={handleRemoveAllSensors}
              />
            </TabsContent>

            {/* Clause Tab */}
            <TabsContent value="clause" className="space-y-4 mt-4">
              <div className="grid gap-2">
                <Label>{t('fields.contractualClause')}</Label>
                <Textarea
                  value={form.contractualClause || ''}
                  onChange={(e) => setForm({ ...form, contractualClause: e.target.value })}
                  placeholder={t('fields.contractualClausePlaceholder')}
                  rows={12}
                />
              </div>
            </TabsContent>
          </Tabs>

      {/* Sensor Select Dialog */}
      {contract?.accountId && (
        <SensorSelectDialog
          open={sensorSelectOpen}
          onOpenChange={setSensorSelectOpen}
          pgAccountId={contract.accountId}
          linkedSensorIds={linkedSensorIds}
          onAddSensor={handleAddSensor}
          onRemoveSensor={(sensorId) => handleRemoveSensor(sensorId)}
        />
      )}

      {/* New quote for this subscription's account (Hugo #1084 p5) — creates then links it */}
      {contract && contract.accountId && (
        <QuoteFormModal
          open={newQuoteOpen}
          onOpenChange={(open) => { setNewQuoteOpen(open); if (!open) createdQuotePgIdRef.current = null; }}
          onSubmit={handleCreateSubscriptionQuote}
          initialCustomerData={{
            pgAccountId: contract.accountId,
            accountName: contract.accountName || undefined,
            salesManagerId: contract.salesManagerId || undefined,
            contractId: contract.id,
            lockCustomer: true,
          }}
          isLoading={createQuoteMutation.isPending || updateQuoteMutation.isPending}
        />
      )}
    </EntityFormDialog>
  );
}
